import { inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { SegmentedVariablesRepository } from '@data/repositories';
import { SegmentedVariable } from '../../data/entities';
import type { ISegmentedZoneItemListByOperation } from '@domain/interfaces';
import type { TSegmentedZoneParams } from '@data/types';
import { sumPercentagesValidator } from '@domain/validators';
import { switchList } from '@shared/utils';
import { FetchingOptions } from '../../data/constants/fetching-options';
import { segmentedVariableMapper } from '../../utils/segmented-variable.utils';
import { FiltersProvider } from '@data/providers';

type TCreateSegmentedZoneState = {
  fetchStack: FetchingOptions[];
  segmentedVariableList: SegmentedVariable[];
  segmentedVariableOptions: SegmentedVariable[];
  defaultVariable: SegmentedVariable;
  segmentedZone: ISegmentedZoneItemListByOperation | null;
};

const initialState: TCreateSegmentedZoneState = {
  fetchStack: [],
  segmentedVariableList: [],
  segmentedVariableOptions: [],
  defaultVariable: SegmentedVariable.fromJson({}),
  segmentedZone: null,
};

@Injectable({
  providedIn: 'root',
})
export class CreateSegmentedZoneProvider {
  readonly #fb = inject(FormBuilder);
  readonly #segmentedRepository = inject(SegmentedVariablesRepository);
  readonly #filters = inject(FiltersProvider);

  readonly #state = signalState(initialState);
  readonly formArray = this.#fb.array<FormGroup>([], {
    validators: sumPercentagesValidator('percentage'),
  });

  readonly formGroupZone = this.#fb.group({
    name: this.#fb.control<string>('', [Validators.required]),
    filial_id: this.#fb.control<string>('', [Validators.required]),
    filial_name: this.#fb.control<string>(''),
    segmented_zone: this.#fb.control<string>(''),
    operation_id: this.#fb.control<string>('', [Validators.required]),
  });

  readonly segmentedVariableList = this.#state.segmentedVariableList;
  readonly segmentedZone = this.#state.segmentedZone;
  readonly segmentedVariableOptions = this.#state.segmentedVariableOptions;

  async getDefaultVariable() {
    const result =
      await this.#segmentedRepository.getSegmentedDefaultVariable();

    if (result.isOk === true) {
      patchState(this.#state, {
        defaultVariable: result.data,
      });
    }
  }

  changeSegmentedVariables(items: SegmentedVariable[]) {
    patchState(this.#state, { segmentedVariableList: items });
  }

  addSelectedVariables(items: SegmentedVariable[]) {
    const segmentedVariableList = segmentedVariableMapper(
      this.segmentedVariableList(),
    ).merge(items);

    patchState(this.#state, { segmentedVariableList });
    this.addFormControl(segmentedVariableList);
  }

  async addVariableFromName(name: string) {
    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).add(FetchingOptions.ON_CREATE),
    }));

    const result =
      await this.#segmentedRepository.createSegmentedVariableByName(name);

    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).remove(FetchingOptions.ON_CREATE),
    }));

    if (result.isOk === false) {
      throw result.error;
    }

    const newItemList = segmentedVariableMapper(
      this.#state.segmentedVariableList(),
    ).add(result.data);

    patchState(this.#state, { segmentedVariableList: newItemList });
    this.addFormControl(newItemList);
  }

  removeVariableFromId(id: string) {
    patchState(this.#state, ({ segmentedVariableList }) => {
      const newListSegmentedVariable = segmentedVariableMapper(
        segmentedVariableList,
      ).removeById(id);

      this.addFormControl(newListSegmentedVariable);

      return {
        segmentedVariableList: newListSegmentedVariable,
      };
    });
  }

  getArrayValuesMap() {
    const prevValues: [
      string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Record<'id' | 'percentage' | 'recalculate', any>,
    ][] = this.formArray.value.map((fa) => [fa.id, fa]);

    return new Map(prevValues);
  }

  addFormControl(itemList: SegmentedVariable[]) {
    const mapValues = this.getArrayValuesMap();
    this.formArray.clear();

    itemList.forEach((item) => {
      const formGroup = this.#fb.group({
        id: this.#fb.control(item.id),
        percentage: this.#fb.control(
          mapValues.has(item.id)
            ? mapValues.get(item.id)?.['percentage']
            : item.percentage,
        ),
        recalculate: this.#fb.control(
          mapValues.has(item.id)
            ? mapValues.get(item.id)?.['recalculate']
            : item.recalculate,
        ),
      });

      this.formArray.push(formGroup);
    });

    this.formArray.updateValueAndValidity();
  }

  setSegmentedZone(sz: ISegmentedZoneItemListByOperation | null) {
    const newSegmentedList =
      !!sz && sz?.segmentedVariables?.length
        ? segmentedVariableMapper(
            sz.segmentedVariables.map(SegmentedVariable.fromApi),
          ).sortBy()
        : [this.#state.defaultVariable()];

    patchState(this.#state, {
      segmentedZone: sz,
      segmentedVariableList: newSegmentedList,
    });

    this.addFormControl(newSegmentedList);

    if (sz) {
      this.formGroupZone.setValue(
        {
          name: sz.name,
          filial_id: sz.filial.code,
          filial_name: sz.filial.name,
          segmented_zone: sz.code,
          operation_id: sz.operations?.at(0)?.id,
        },
        { emitEvent: false },
      );
    }
  }

  getFormData(): Omit<TSegmentedZoneParams, 'segmentedNets'> {
    const formValues = this.formGroupZone.value;
    const mapValues = this.getArrayValuesMap();
    const listItems = this.#state.segmentedVariableList();

    const finalValue = {
      id: this.segmentedZone()?.id,
      zoneId: formValues.filial_id,
      name: formValues.name,
      operationId: formValues.operation_id,
      segmentedVariables: listItems.map((item) =>
        item
          .copyWith({
            percentage: mapValues.get(item.id).percentage,
            recalculate: mapValues.get(item.id).recalculate,
          })
          .toJson(),
      ),
    };

    return finalValue;
  }

  async searchSegmentedVariable(name: string) {
    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).add(FetchingOptions.FETCH_SEARCH),
    }));

    const result =
      await this.#segmentedRepository.searchSegmentedVariable(name);

    patchState(this.#state, ({ fetchStack }) => ({
      fetchStack: switchList(fetchStack).remove(FetchingOptions.FETCH_SEARCH),
    }));

    if (result.isOk === false) {
      return result;
    }

    patchState(this.#state, {
      segmentedVariableOptions: result.data,
    });

    return result;
  }

  isLoadingBy(key: FetchingOptions) {
    return switchList(this.#state.fetchStack()).find(key);
  }

  clearOptionsList() {
    patchState(this.#state, {
      segmentedVariableOptions: [],
    });
  }

  changeRecalculate(id: string, event: number) {
    const recalculate = this.#filters
      .recalculate()
      .find((r) => r.id === event)!;

    patchState(this.#state, ({ segmentedVariableList }) => ({
      segmentedVariableList: segmentedVariableMapper(
        segmentedVariableList,
      ).changeRecalculate(id, recalculate),
    }));
  }

  async clear() {
    const defaultVariable = this.#state.defaultVariable();
    patchState(this.#state, {
      ...initialState,
      defaultVariable,
    });
    this.formArray.clear();
    this.formGroupZone.reset();
  }
}
